/**
 * Count data sources the same way the DataSources drawer Active chip does:
 * Layers/Files expand to selected item counts; other sources count as 1.
 */
export function countSitrepDataSources(sources: unknown): number {
  if (!Array.isArray(sources) || sources.length === 0) return 0;
  return sources.reduce((n, ds) => {
    const rec = ds as {
      source?: string;
      selectedLayers?: unknown[];
      selectedFileIds?: unknown[];
      fileIds?: unknown[];
      selectedFiles?: unknown[];
    };
    if (
      rec.source === 'Layers' &&
      Array.isArray(rec.selectedLayers) &&
      rec.selectedLayers.length > 0
    ) {
      return n + rec.selectedLayers.length;
    }
    if (rec.source === 'Files') {
      const ids = rec.selectedFiles ?? rec.selectedFileIds ?? rec.fileIds;
      if (Array.isArray(ids) && ids.length > 0) return n + ids.length;
    }
    return n + 1;
  }, 0);
}

type DataSourceRec = {
  source?: string;
  selectedLayers?: unknown[];
  [key: string]: unknown;
};

function extractLayersDataSource(sources: unknown[]): DataSourceRec | null {
  return (
    (sources.find((ds) => (ds as DataSourceRec)?.source === 'Layers') as
      | DataSourceRec
      | undefined) || null
  );
}

/**
 * Upsert parent Layers onto a child list (preserves Incident and other
 * sources).
 */
export function upsertLayersDataSource(
  dataSources: unknown,
  layersEntry: DataSourceRec
): unknown[] {
  const next = Array.isArray(dataSources)
    ? [...(dataSources as DataSourceRec[])]
    : [];
  const entry: DataSourceRec = { ...layersEntry, source: 'Layers' };
  const idx = next.findIndex((ds) => ds?.source === 'Layers');
  if (idx >= 0) {
    next[idx] = { ...next[idx], ...entry, source: 'Layers' };
  } else {
    next.push(entry);
  }
  return next;
}

/**
 * Effective data_sources for display / Edit Settings (matches COMS generate
 * inherit):
 *
 * - Empty own → full parent list (Situation a–e / CONOPS)
 * - Non-empty own → upsert parent Layers only (Tasks Incident + parent Layers)
 */
export function resolveEffectiveDataSources(
  ownSources: unknown,
  parentSources?: unknown
): unknown[] {
  const own = Array.isArray(ownSources) ? [...ownSources] : [];
  const parent = Array.isArray(parentSources) ? parentSources : [];
  if (own.length === 0) {
    return parent.length > 0 ? [...parent] : own;
  }
  const parentLayers = extractLayersDataSource(parent);
  if (!parentLayers) return own;
  return upsertLayersDataSource(own, parentLayers);
}
